import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import config from '../../config/config';
import { useAuth } from '../../context/AuthContext';

const ROLES = ['admin', 'analyst', 'viewer'];
const ROLE_BADGE = { admin: '#ef4444', analyst: '#3b82f6', viewer: '#64748b' };

export default function UserManagement() {
  const { token, register } = useAuth();
  const ssoHintStyle = { marginTop: 6, fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary)' };
  const azureExampleIssuer = 'https://login.microsoftonline.com/<tenant-id>/v2.0';
  const defaultRedirectExample = `${config.BASE_URL}/auth/oidc/callback`;
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [ssoSettings, setSsoSettings] = useState({
    oidcEnabled: false,
    oidcProviderName: 'Microsoft Entra ID',
    oidcIssuerUrl: '',
    oidcClientId: '',
    oidcClientSecret: '',
    oidcClientSecretPresent: false,
    oidcRedirectUrl: '',
    oidcScopes: 'openid profile email',
    oidcAllowedDomains: '',
    oidcDefaultRole: 'viewer',
    oidcAutoProvision: true,
  });
  const [jiraSettings, setJiraSettings] = useState({
    jiraEnabled: false,
    jiraBaseUrl: '',
    jiraUserEmail: '',
    jiraApiToken: '',
    jiraApiTokenPresent: false,
    jiraProjectKey: '',
    jiraIssueType: 'Task',
  });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', role: 'analyst' });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [usersError, setUsersError] = useState('');
  const [auditError, setAuditError] = useState('');
  const [actionError, setActionError] = useState('');
  const [ssoError, setSsoError] = useState('');
  const [ssoSuccess, setSsoSuccess] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const [jiraError, setJiraError] = useState('');
  const [jiraSuccess, setJiraSuccess] = useState('');
  const [jiraLoading, setJiraLoading] = useState(false);
  const [githubSettings, setGithubSettings] = useState({
    githubEnabled: false,
    githubPat: '',
    githubPatPresent: false,
    githubDefaultOwner: '',
    githubDefaultRepo: '',
  });
  const [githubError, setGithubError] = useState('');
  const [githubSuccess, setGithubSuccess] = useState('');
  const [githubLoading, setGithubLoading] = useState(false);

  const tab = searchParams.get('admin_tab') || 'users';
  const setTab = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('admin_tab', val); return n; });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadUsers = useCallback(() => {
    setLoading(true);
    setUsersError('');
    fetch(`${config.BASE_URL}/admin/users`, { headers })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || 'Failed to load users.');
        return data;
      })
      .then(setUsers)
      .catch((e) => {
        setUsers([]);
        setUsersError(e.message || 'Failed to load users.');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadAudit = useCallback(() => {
    setAuditError('');
    fetch(`${config.BASE_URL}/admin/audit-log?limit=100`, { headers })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || 'Failed to load audit log.');
        return data;
      })
      .then(setAuditLog)
      .catch((e) => {
        setAuditLog([]);
        setAuditError(e.message || 'Failed to load audit log.');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadSSOSettings = useCallback(() => {
    setSsoError('');
    setSsoLoading(true);
    fetch(`${config.BASE_URL}/auth/sso/settings`, { headers })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || 'Failed to load SSO settings.');
        return data;
      })
      .then((data) => {
        setSsoSettings((prev) => ({
          ...prev,
          ...data,
          oidcClientSecret: '',
        }));
      })
      .catch((e) => setSsoError(e.message || 'Failed to load SSO settings.'))
      .finally(() => setSsoLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { if (tab === 'audit') loadAudit(); }, [tab, loadAudit]);
  useEffect(() => { if (tab === 'sso') loadSSOSettings(); }, [tab, loadSSOSettings]);

  const loadJiraSettings = useCallback(() => {
    setJiraError('');
    setJiraLoading(true);
    fetch(`${config.BASE_URL}/integrations/jira/settings`, { headers })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || 'Failed to load Jira settings.');
        return data;
      })
      .then((data) => {
        setJiraSettings((prev) => ({
          ...prev,
          ...data,
          jiraApiToken: '',
        }));
      })
      .catch((e) => setJiraError(e.message || 'Failed to load Jira settings.'))
      .finally(() => setJiraLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (tab === 'jira') loadJiraSettings(); }, [tab, loadJiraSettings]);

  const loadGithubSettings = useCallback(() => {
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${config.BASE_URL}/integrations/github/settings`, { headers })
      .then(r => r.json())
      .then(data => {
        setGithubSettings(prev => ({
          ...prev,
          githubEnabled: data.githubEnabled || false,
          githubPatPresent: data.githubPatPresent || false,
          githubDefaultOwner: data.githubDefaultOwner || '',
          githubDefaultRepo: data.githubDefaultRepo || '',
        }));
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => { if (tab === 'github') loadGithubSettings(); }, [tab, loadGithubSettings]);

  const handleRoleChange = async (id, role) => {
    try {
      const response = await fetch(`${config.BASE_URL}/admin/users/${id}/role`, {
        method: 'PUT', headers,
        body: JSON.stringify({ role }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Failed to update user role.');
      loadUsers();
    } catch (e) {
      setActionError(e.message || 'Failed to update user role.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    setActionError('');
    try {
      const response = await fetch(`${config.BASE_URL}/admin/users/${id}`, { method: 'DELETE', headers });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Failed to delete user.');
      loadUsers();
    } catch (e) {
      setActionError(e.message || 'Failed to delete user.');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormError(''); setFormSuccess('');
    try {
      await register(form.email, form.password, form.role);
      setFormSuccess(`User ${form.email} created`);
      setForm({ email: '', password: '', role: 'analyst' });
      loadUsers();
    } catch (err) {
      setFormError(err.message);
    }
  };

  const handleSaveSSO = async (e) => {
    e.preventDefault();
    setSsoError('');
    setSsoSuccess('');
    setSsoLoading(true);
    try {
      const response = await fetch(`${config.BASE_URL}/auth/sso/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(ssoSettings),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Failed to save SSO settings.');
      setSsoSettings((prev) => ({
        ...prev,
        ...data,
        oidcClientSecret: '',
      }));
      setSsoSuccess('Enterprise sign-in settings saved.');
    } catch (e) {
      setSsoError(e.message || 'Failed to save SSO settings.');
    } finally {
      setSsoLoading(false);
    }
  };

  const handleSaveJira = async (e) => {
    e.preventDefault();
    setJiraError('');
    setJiraSuccess('');
    setJiraLoading(true);
    try {
      const response = await fetch(`${config.BASE_URL}/integrations/jira/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(jiraSettings),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Failed to save Jira settings.');
      setJiraSettings((prev) => ({
        ...prev,
        ...data,
        jiraApiToken: '',
      }));
      setJiraSuccess('Jira integration settings saved.');
    } catch (e) {
      setJiraError(e.message || 'Failed to save Jira settings.');
    } finally {
      setJiraLoading(false);
    }
  };

  const handleSaveGithub = async (e) => {
    e.preventDefault();
    setGithubLoading(true);
    setGithubError('');
    setGithubSuccess('');
    try {
      const response = await fetch(`${config.BASE_URL}/integrations/github/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(githubSettings),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Failed to save');
      setGithubSettings(prev => ({
        ...prev,
        githubPatPresent: data.githubPatPresent || prev.githubPatPresent,
        githubPat: '',
      }));
      setGithubSuccess('GitHub settings saved successfully.');
    } catch (err) {
      setGithubError(err.message);
    } finally {
      setGithubLoading(false);
    }
  };

  const handleCopyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setSsoSuccess(`${label} copied to clipboard.`);
    } catch (e) {
      setSsoError(`Failed to copy ${label.toLowerCase()}.`);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <div className="ce-page-header mb-1">
        <h1 className="ce-page-title">👥 User Management</h1>
      </div>
      <p style={{ color: 'var(--bs-secondary-color)', marginBottom: 24 }}>
        Manage DepGuard users, roles and audit activity.
      </p>

      {actionError && (
        <div className="alert alert-danger py-2">{actionError}</div>
      )}

      <div className="d-flex gap-2 mb-4">
        {['users', 'invite', 'audit', 'sso', 'jira', 'github'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`ce-btn ${tab === t ? 'ce-btn--primary' : 'ce-btn--secondary'}`}>
            {t === 'users' ? '👤 Users' : t === 'invite' ? '➕ Invite' : t === 'audit' ? '📋 Audit Log' : t === 'sso' ? '🏢 SSO' : t === 'github' ? '🐙 GitHub Issues' : '🎫 Jira'}
          </button>
        ))}
      </div>

      {tab === 'users' && (
        <div className="ce-card">
          <div className="ce-card-header ce-section-header">
            <span className="ce-card-title ce-section-title">Users</span>
          </div>
          {usersError && <div className="alert alert-danger py-2 mx-3 mt-2">{usersError}</div>}
          {loading && <p className="px-3 pt-2" style={{ color: 'var(--bs-secondary-color)' }}>Loading…</p>}
          <div className="ce-card-body--flush">
            <div className="ce-table-wrap">
              <table className="ce-table">
                <thead>
                  <tr>
                    <th>EMAIL</th>
                    <th>ROLE</th>
                    <th>CREATED</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {(users || []).map(u => (
                    <tr key={u.id}>
                      <td>{u.email}</td>
                      <td>
                        <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)}
                          className="ce-filter-select"
                          style={{
                            background: ROLE_BADGE[u.role] + '22',
                            color: ROLE_BADGE[u.role],
                            border: `1px solid ${ROLE_BADGE[u.role]}55`,
                            fontWeight: 600, fontSize: 12, padding: '2px 8px',
                          }}>
                          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td style={{ color: 'var(--bs-secondary-color)', fontSize: 12 }}>
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <button onClick={() => handleDelete(u.id)}
                          className="ce-btn ce-btn--danger ce-btn--sm"
                          title="Delete user">
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'invite' && (
        <div className="ce-card" style={{ maxWidth: 420 }}>
          <div className="ce-card-header">
            <span className="ce-card-title">Create New User</span>
          </div>
          <div className="ce-card-body">
          <form onSubmit={handleCreateUser} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>EMAIL</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required placeholder="user@example.com"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>PASSWORD</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                required minLength={6} placeholder="min. 6 characters"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>ROLE</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                <option value="analyst">Analyst</option>
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {formError && <div style={{ color: '#f87171', fontSize: 13, background: 'rgba(239,68,68,0.1)', padding: '8px 12px', borderRadius: 6 }}>{formError}</div>}
            {formSuccess && <div style={{ color: '#4ade80', fontSize: 13, background: 'rgba(74,222,128,0.1)', padding: '8px 12px', borderRadius: 6 }}>✅ {formSuccess}</div>}
            <button type="submit" className="ce-btn ce-btn--primary">
              ➕ Create User
            </button>
          </form>
          </div>
        </div>
      )}

      {tab === 'audit' && (
        <div className="ce-card">
          <div className="ce-card-header">
            <span className="ce-card-title">Audit Log</span>
          </div>
          {auditError && <div className="alert alert-danger py-2 mx-3 mt-2">{auditError}</div>}
          <div className="ce-card-body--flush">
            <div className="ce-table-wrap">
              <table className="ce-table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    {['TIME', 'USER', 'ACTION', 'RESOURCE', 'IP'].map(h => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(auditLog || []).map(e => (
                    <tr key={e.id}>
                      <td style={{ color: 'var(--bs-secondary-color)', fontSize: 11 }}>
                        {new Date(e.createdAt).toLocaleString()}
                      </td>
                      <td>{e.userEmail}</td>
                      <td>
                        <span className="ce-tag">{e.action}</span>
                      </td>
                      <td style={{ color: 'var(--bs-secondary-color)', fontSize: 11 }}>
                        {e.resourceType} {e.resourceId && `#${e.resourceId}`}
                      </td>
                      <td style={{ color: 'var(--bs-secondary-color)', fontSize: 11 }}>{e.ipAddress}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'sso' && (
        <div className="ce-card" style={{ maxWidth: 720 }}>
          <div className="ce-card-header">
            <span className="ce-card-title">Enterprise Sign-In</span>
          </div>
          <div className="ce-card-body">
          <p style={{ color: 'var(--bs-secondary-color)', marginBottom: 20 }}>
            Configure OIDC / Azure AD style login while keeping the existing local admin login available as a fallback.
          </p>
          <div style={{ marginBottom: 18, padding: '12px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.22)' }}>
            <div style={{ color: 'var(--text-primary)', fontWeight: 700, marginBottom: 8 }}>Azure / Entra ID quick example</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6 }}>
              Issuer URL example: <code>{azureExampleIssuer}</code><br />
              Redirect URL example: <code>{defaultRedirectExample}</code>
            </div>
          </div>
          {ssoError && <div className="alert alert-danger py-2">{ssoError}</div>}
          {ssoSuccess && <div className="alert alert-success py-2">{ssoSuccess}</div>}
          <form onSubmit={handleSaveSSO} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                id="oidcEnabled"
                type="checkbox"
                checked={ssoSettings.oidcEnabled}
                onChange={e => setSsoSettings(s => ({ ...s, oidcEnabled: e.target.checked }))}
              />
              <label htmlFor="oidcEnabled" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Enable enterprise OIDC sign-in</label>
            </div>
            <div style={{ ...ssoHintStyle, gridColumn: '1 / -1', marginTop: -6 }}>
              Turns on the enterprise sign-in button on the login page while keeping local login available as a fallback.
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>PROVIDER NAME</label>
              <input
                value={ssoSettings.oidcProviderName}
                onChange={e => setSsoSettings(s => ({ ...s, oidcProviderName: e.target.value }))}
                placeholder="Microsoft Entra ID"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Shown to users on the login button, for example `Microsoft Entra ID` or `Okta`.</div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>DEFAULT ROLE</label>
              <select
                value={ssoSettings.oidcDefaultRole}
                onChange={e => setSsoSettings(s => ({ ...s, oidcDefaultRole: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              >
                <option value="viewer">Viewer</option>
                <option value="analyst">Analyst</option>
                <option value="admin">Admin</option>
              </select>
              <div style={ssoHintStyle}>Applied to newly auto-provisioned enterprise users unless you later change their role manually.</div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 0 }}>ISSUER URL</label>
                <button
                  type="button"
                  onClick={() => handleCopyValue(azureExampleIssuer, 'Issuer URL example')}
                  style={{ border: 'none', background: 'none', color: 'var(--accent,#3b82f6)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >
                  Copy Azure example
                </button>
              </div>
              <input
                value={ssoSettings.oidcIssuerUrl}
                onChange={e => setSsoSettings(s => ({ ...s, oidcIssuerUrl: e.target.value }))}
                placeholder="https://login.microsoftonline.com/<tenant-id>/v2.0"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>
                Use the base OIDC issuer URL, not the full metadata URL. For Entra ID, copy the OpenID Connect metadata document URL and remove
                <code> /.well-known/openid-configuration</code>.
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>CLIENT ID</label>
              <input
                value={ssoSettings.oidcClientId}
                onChange={e => setSsoSettings(s => ({ ...s, oidcClientId: e.target.value }))}
                placeholder="Application (client) ID"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Paste the Application (client) ID from your identity-provider app registration.</div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                CLIENT SECRET {ssoSettings.oidcClientSecretPresent ? '(leave blank to keep existing)' : ''}
              </label>
              <input
                type="password"
                value={ssoSettings.oidcClientSecret}
                onChange={e => setSsoSettings(s => ({ ...s, oidcClientSecret: e.target.value }))}
                placeholder={ssoSettings.oidcClientSecretPresent ? 'Stored secret present' : 'Client secret'}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Use the secret value generated for the same app registration. Leave this blank during edits if you want to keep the stored secret.</div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 0 }}>REDIRECT URL</label>
                <button
                  type="button"
                  onClick={() => handleCopyValue(defaultRedirectExample, 'Redirect URL example')}
                  style={{ border: 'none', background: 'none', color: 'var(--accent,#3b82f6)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >
                  Copy callback URL
                </button>
              </div>
              <input
                value={ssoSettings.oidcRedirectUrl}
                onChange={e => setSsoSettings(s => ({ ...s, oidcRedirectUrl: e.target.value }))}
                placeholder="http://localhost:8080/auth/oidc/callback"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Register this exact callback URL in your identity provider so it can redirect users back into DepGuard after sign-in.</div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>SCOPES</label>
              <input
                value={ssoSettings.oidcScopes}
                onChange={e => setSsoSettings(s => ({ ...s, oidcScopes: e.target.value }))}
                placeholder="openid profile email"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Space-separated OIDC scopes. `openid profile email` is the normal starting point for user sign-in.</div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>ALLOWED DOMAINS</label>
              <input
                value={ssoSettings.oidcAllowedDomains}
                onChange={e => setSsoSettings(s => ({ ...s, oidcAllowedDomains: e.target.value }))}
                placeholder="company.com, subsidiary.eu"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Optional comma-separated email domains. Leave empty to allow any domain returned by the provider.</div>
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                id="oidcAutoProvision"
                type="checkbox"
                checked={ssoSettings.oidcAutoProvision}
                onChange={e => setSsoSettings(s => ({ ...s, oidcAutoProvision: e.target.checked }))}
              />
              <label htmlFor="oidcAutoProvision" style={{ color: 'var(--text-primary)' }}>
                Auto-provision new enterprise users with the default role
              </label>
            </div>
            <div style={{ ...ssoHintStyle, gridColumn: '1 / -1', marginTop: -6 }}>
              When enabled, first-time SSO users are created automatically. When disabled, only already-linked users can sign in.
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10 }}>
              <button
                type="submit"
                disabled={ssoLoading}
                className="ce-btn ce-btn--primary"
              >
                {ssoLoading ? 'Saving…' : '💾 Save enterprise sign-in'}
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      {tab === 'jira' && (
        <div className="ce-card" style={{ maxWidth: 720 }}>
          <div className="ce-card-header">
            <span className="ce-card-title">Jira Integration</span>
          </div>
          <div className="ce-card-body">
          <p style={{ color: 'var(--bs-secondary-color)', marginBottom: 20 }}>
            Configure Jira so remediation items can open a ticket and sync its current status back into DepGuard.
          </p>
          {jiraError && <div className="alert alert-danger py-2">{jiraError}</div>}
          {jiraSuccess && <div className="alert alert-success py-2">{jiraSuccess}</div>}
          <form onSubmit={handleSaveJira} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                id="jiraEnabled"
                type="checkbox"
                checked={jiraSettings.jiraEnabled}
                onChange={e => setJiraSettings(s => ({ ...s, jiraEnabled: e.target.checked }))}
              />
              <label htmlFor="jiraEnabled" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Enable Jira ticket sync</label>
            </div>
            <div style={{ ...ssoHintStyle, gridColumn: '1 / -1', marginTop: -6 }}>
              Allows tracked remediation items to create Jira issues and refresh their ticket status from Jira.
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>JIRA BASE URL</label>
              <input
                value={jiraSettings.jiraBaseUrl}
                onChange={e => setJiraSettings(s => ({ ...s, jiraBaseUrl: e.target.value }))}
                placeholder="https://your-company.atlassian.net"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Your Jira site base URL, without `/rest/api/...` on the end.</div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>USER EMAIL</label>
              <input
                value={jiraSettings.jiraUserEmail}
                onChange={e => setJiraSettings(s => ({ ...s, jiraUserEmail: e.target.value }))}
                placeholder="jira-admin@company.com"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Email address used together with the Jira API token for Basic authentication.</div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                API TOKEN {jiraSettings.jiraApiTokenPresent ? '(leave blank to keep existing)' : ''}
              </label>
              <input
                type="password"
                value={jiraSettings.jiraApiToken}
                onChange={e => setJiraSettings(s => ({ ...s, jiraApiToken: e.target.value }))}
                placeholder={jiraSettings.jiraApiTokenPresent ? 'Stored token present' : 'Jira API token'}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Create this in Atlassian account security. It is stored server-side and hidden on reload.</div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>PROJECT KEY</label>
              <input
                value={jiraSettings.jiraProjectKey}
                onChange={e => setJiraSettings(s => ({ ...s, jiraProjectKey: e.target.value.toUpperCase() }))}
                placeholder="SEC"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Use the Jira project key, for example <code>SEC</code>. Do not enter a project name or an issue key like <code>SEC-123</code>.</div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>ISSUE TYPE</label>
              <input
                value={jiraSettings.jiraIssueType}
                onChange={e => setJiraSettings(s => ({ ...s, jiraIssueType: e.target.value }))}
                placeholder="Task"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
              />
              <div style={ssoHintStyle}>Typical values are `Task`, `Bug`, or a custom issue type available in your Jira project.</div>
            </div>

            <div style={{ gridColumn: '1 / -1', padding: '12px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.22)' }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: 700, marginBottom: 6 }}>How the remediation flow works</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6 }}>
                Track a finding in the Remediation tab first. Then use <strong>Create Jira</strong> to open an issue or <strong>Sync Jira</strong> to refresh its status.
                DepGuard stores the ticket key, browse URL, and last synced Jira status on the remediation record.
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10 }}>
              <button
                type="submit"
                disabled={jiraLoading}
                className="ce-btn ce-btn--primary"
              >
                {jiraLoading ? 'Saving…' : '💾 Save Jira settings'}
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      {tab === 'github' && (
        <div className="ce-card" style={{ maxWidth: 720 }}>
          <div className="ce-card-header">
            <span className="ce-card-title">🐙 GitHub Issues Integration</span>
          </div>
          <div className="ce-card-body">
            <p style={{ color: 'var(--bs-secondary-color)', marginBottom: 20 }}>
              Configure GitHub so remediation items can open GitHub Issues on the repository of origin and sync their status back into DepGuard.
            </p>
            {githubError && <div className="alert alert-danger py-2">{githubError}</div>}
            {githubSuccess && <div className="alert alert-success py-2">{githubSuccess}</div>}
            <form onSubmit={handleSaveGithub} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  id="githubEnabled"
                  type="checkbox"
                  checked={githubSettings.githubEnabled}
                  onChange={e => setGithubSettings(s => ({ ...s, githubEnabled: e.target.checked }))}
                />
                <label htmlFor="githubEnabled" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Enable GitHub Issues integration</label>
              </div>
              <div style={{ ...ssoHintStyle, gridColumn: '1 / -1', marginTop: -6 }}>
                Allows tracked remediation items to open GitHub Issues and sync their open/closed status back into DepGuard.
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  PERSONAL ACCESS TOKEN {githubSettings.githubPatPresent ? '(leave blank to keep existing)' : ''}
                </label>
                <input
                  type="password"
                  value={githubSettings.githubPat}
                  onChange={e => setGithubSettings(s => ({ ...s, githubPat: e.target.value }))}
                  placeholder={githubSettings.githubPatPresent ? 'Stored token present' : 'ghp_...'}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                />
                <div style={ssoHintStyle}>
                  Create a fine-grained PAT at <strong>GitHub → Settings → Developer settings</strong> with <code>issues: write</code> permission on the target repositories. The token is stored server-side.
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>DEFAULT OWNER</label>
                <input
                  value={githubSettings.githubDefaultOwner}
                  onChange={e => setGithubSettings(s => ({ ...s, githubDefaultOwner: e.target.value }))}
                  placeholder="my-org"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                />
                <div style={ssoHintStyle}>GitHub organisation or user name (e.g. <code>my-org</code>). Used when no per-issue override is given.</div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>DEFAULT REPO</label>
                <input
                  value={githubSettings.githubDefaultRepo}
                  onChange={e => setGithubSettings(s => ({ ...s, githubDefaultRepo: e.target.value }))}
                  placeholder="security-backlog"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                />
                <div style={ssoHintStyle}>Default repository name where issues will be created (e.g. <code>security-backlog</code>).</div>
              </div>

              <div style={{ gridColumn: '1 / -1', padding: '12px 14px', borderRadius: 8, background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.22)' }}>
                <div style={{ color: 'var(--text-primary)', fontWeight: 700, marginBottom: 6 }}>How the GitHub Issues flow works</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6 }}>
                  Track a finding in the Remediation tab first. Then use <strong>Create GitHub Issue</strong> to open an issue or <strong>Sync GitHub</strong> to refresh its open/closed state.
                  DepGuard stores the issue number, URL, and last known state on the remediation record.
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10 }}>
                <button type="submit" disabled={githubLoading} className="ce-btn ce-btn--primary">
                  {githubLoading ? 'Saving…' : '💾 Save GitHub settings'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
